/**
 * Display formatting.
 *
 * Money is duplicated from `packages/core/src/money.ts` rather than imported,
 * for two reasons worth stating: this is a browser bundle and the core package
 * pulls in the taxonomy and date machinery with it, and the seller endpoint
 * returns `valueMinor` with no currency alongside it, so the formatter here has
 * to survive a null currency where `formatMoney` cannot.
 *
 * The duplication does not extend to the rule. Engineering rule 5 still holds:
 * integer minor units in, string out, and no float anywhere in between —
 * including in `parseMinor`, which is the one place in the dashboard where a
 * human types an amount and a `Number('12.34') * 100` would quietly produce
 * 1233.9999999999998.
 */

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
};

/** Shown wherever a value is genuinely absent, so an empty cell never reads as zero. */
export const NO_VALUE = '—';

/**
 * Minor units to a display string.
 *
 * Null is not zero. A finding with no value has no cost basis for its SKU,
 * which is a real state with a real cause — never render it as $0.00, because a
 * zero-valued claim looks like something not worth filing.
 */
export function formatMinor(minor: number | null | undefined, currency?: string | null): string {
  if (minor === null || minor === undefined) return NO_VALUE;
  const symbol = CURRENCY_SYMBOL[currency ?? 'USD'] ?? `${currency} `;
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}${symbol}${whole.toLocaleString('en-US')}.${cents}`;
}

export class AmountError extends Error {}

/**
 * A typed decimal amount to integer minor units.
 *
 * Used for the verified recovery amount on `mark_paid`. Parsed digit-wise
 * rather than through `Number`, so the value that reaches the API — and from
 * there an invoice at 15% of it — is exactly what the operator read off the
 * Finance screen.
 */
export function parseMinor(value: string): number {
  // Thousands separators are allowed only where they are actually separators.
  // Stripping every comma first would quietly turn "12," into $12.00, and an
  // amount that was mistyped must be refused rather than repaired.
  const trimmed = value.trim().replace(/\s/g, '');
  const match = /^(-?)(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) {
    throw new AmountError(
      `"${value}" is not an amount. Enter it as it appears in Finance — digits and at most two decimals.`,
    );
  }
  // The defaults are unreachable — both groups match whenever the regex does —
  // but `noUncheckedIndexedAccess` cannot know that, and a cast would hide a
  // real change to the pattern later.
  const [, sign, whole = '0', fraction = ''] = match;
  const minor = Number(whole.replace(/,/g, '')) * 100 + Number(fraction.padEnd(2, '0'));
  return sign === '-' ? -minor : minor;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * An ISO calendar day as `12 Aug 2026`.
 *
 * Formatted from the string rather than through `new Date()`, because every
 * date the API sends is already a UTC calendar day and parsing it into a local
 * Date can shift it by one — which on an expiry date is the difference between
 * a claim that can be filed and one that cannot.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const [year, month, day] = iso.slice(0, 10).split('-');
  const index = Number(month) - 1;
  if (!year || !day || !MONTHS[index]) return iso;
  return `${Number(day)} ${MONTHS[index]} ${year}`;
}

/** An instant, in the reader's own timezone — audit rows are read by a person at a desk. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(date.toISOString())}, ${date.toTimeString().slice(0, 5)}`;
}

/** `evidence_pending` to `Evidence pending`. Enum values are stored, not shown. */
export function humanize(value: string | null | undefined): string {
  if (!value) return NO_VALUE;
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
